// Canwood Botanical — lightweight parallax for banner media.
// Any element with [data-cb-parallax] gets its `.cb-parallax__media` child
// translated on scroll. rAF-throttled, only runs for on-screen banners, and
// disabled entirely under prefers-reduced-motion.

(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {Set<HTMLElement>} */
  const active = new Set();
  /** @type {Set<HTMLElement>} */
  const visible = new Set();
  let ticking = false;
  /** @type {IntersectionObserver | null} */
  let io = null;

  const strengthOf = (el) => {
    const raw = parseFloat(el.getAttribute('data-cb-parallax-strength') || '0.18');
    // clamp to a tasteful range
    return Math.max(0, Math.min(0.4, isNaN(raw) ? 0.18 : raw));
  };

  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    for (const el of visible) {
      const media = el.querySelector('.cb-parallax__media');
      if (!(media instanceof HTMLElement)) continue;
      const rect = el.getBoundingClientRect();
      // progress: -1 (below viewport) → 1 (above viewport), 0 at center
      const center = rect.top + rect.height / 2;
      const progress = (center - vh / 2) / (vh / 2 + rect.height / 2);
      const shift = -progress * strengthOf(el) * rect.height;
      media.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
    }
  };

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  const register = (el) => {
    if (active.has(el)) return;
    active.add(el);
    io?.observe(el);
  };

  const scan = (root = document) => {
    root.querySelectorAll('[data-cb-parallax]').forEach((el) => {
      if (el instanceof HTMLElement) register(el);
    });
  };

  const init = () => {
    if (reduceMotion.matches) return; // media stays static via CSS

    io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target;
          if (!(el instanceof HTMLElement)) continue;
          if (entry.isIntersecting) visible.add(el);
          else visible.delete(el);
        }
        if (visible.size) requestUpdate();
      },
      { threshold: 0 }
    );

    scan();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    requestUpdate();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!io) return;
    const target = event.target instanceof HTMLElement ? event.target : document;
    scan(target);
    requestUpdate();
  });
})();
